import { type ComponentProps, useRef } from 'react';

import {
  type BackgroundProps,
  Button,
  type ColorProps,
  type TypographyProps,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  type ActiveModifiers,
  type DayProps,
  useDayRender,
} from 'react-day-picker';

const getDisabledStyle = (): ComponentProps<typeof Button> => ({
  _disabled: {
    border: 'transparent',
    bg: 'transparent',
    color: 'gray.400',
    cursor: 'default',
  },
});

const getHoverStyles = (
  activeModifiers: ActiveModifiers
): ComponentProps<typeof Button> => ({
  _hover: {
    bg: activeModifiers.selected ? 'brand.700' : 'brand.50',
    color: activeModifiers.selected ? 'white' : 'brand.700',
    fontWeight:
      activeModifiers.today || activeModifiers.selected ? 'bold' : 'semibold',
  },
});

const getButtonStyles = (
  activeModifiers: ActiveModifiers
): ComponentProps<typeof Button> => {
  const disabledStyle = getDisabledStyle();

  const fontWeight: TypographyProps['fontWeight'] =
    activeModifiers.today || activeModifiers.selected ? 'bold' : 'medium';

  const bg: BackgroundProps['bg'] = activeModifiers.selected
    ? 'brand.500'
    : activeModifiers.today
      ? 'gray.50'
      : 'transparent';

  const color: ColorProps['color'] = activeModifiers.selected
    ? 'white'
    : activeModifiers.today
      ? 'gray.600'
      : undefined;

  const hoverStyles = getHoverStyles(activeModifiers);

  return { ...disabledStyle, fontWeight, bg, color, ...hoverStyles };
};

export const Day = ({ displayMonth, date }: DayProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { buttonProps, isHidden, isButton, divProps, activeModifiers } =
    useDayRender(date, displayMonth, buttonRef);

  // Here we extract the `style` and 'className' attributes in order to not use it
  // It allow us to make custom style easier
  const {
    // eslint-disable-next-line unused-imports/no-unused-vars
    style: _unusedStyle,
    // eslint-disable-next-line unused-imports/no-unused-vars
    className: _unusedClassName,
    ...buttonPropsWithoutStyle
  } = buttonProps;

  if (isHidden) {
    return <></>;
  }

  if (!isButton) {
    return <div {...divProps} />;
  }

  const styles = getButtonStyles(activeModifiers);

  return (
    <Button
      {...buttonPropsWithoutStyle}
      ref={buttonRef}
      variant="outline"
      size="xs"
      borderRadius="full"
      borderColor="transparent"
      shadow="none"
      p={5}
      boxSize={8}
      isDisabled={activeModifiers.disabled}
      {...styles}
    >
      {dayjs(date).format('DD')}
    </Button>
  );
};
